// One row per vendor sub-account on a channel. Renders an enabled
// toggle + an inline editor for the vendor_id (X-Vendor-Id string).
// display_name is rendered as the label and editable inline so admins
// can rename a sub-account without a schema change.
//
// Persistence: PATCH /admin/integrations/:code/vendors/:vendor_pk_id

import { useCallback, useEffect, useState } from "react";
import { Loader2, Check, X, Pencil } from "lucide-react";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import WarehouseMultiSelect from "../WarehouseMultiSelect";
import type { SalesChannelConfig, VendorPatch, VendorSubAccount } from "./types";

interface Props {
    channelCode: string;
    vendor: VendorSubAccount;
    warehouses: { warehouse: string; country?: string | null }[];
    onChannelUpdated: (next: SalesChannelConfig) => void;
    disabled?: boolean;
}

// Mirror of the BE VENDOR_KEY_PATTERN in admin.controller.js. Stops
// obvious typos before the round-trip; the BE re-validates.
const VENDOR_KEY_PATTERN = /^[a-z][a-z0-9_]{0,127}$/;

const VendorSubAccountRow = ({ channelCode, vendor, warehouses, onChannelUpdated, disabled = false }: Props) => {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [editingVendorId, setEditingVendorId] = useState(false);
    const [vendorIdDraft, setVendorIdDraft] = useState(vendor.vendorId);

    useEffect(() => {
        if (!editingVendorId) setVendorIdDraft(vendor.vendorId);
    }, [vendor.vendorId, editingVendorId]);

    const patch = useCallback(
        async (body: VendorPatch): Promise<void> => {
            if (disabled) return;
            setSaving(true);
            setError(null);
            try {
                const res = await apiFetch(
                    `/admin/integrations/${encodeURIComponent(channelCode)}/vendors/${vendor.id}`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(body),
                    },
                );
                const json = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(json?.message ?? `HTTP ${res.status}`);
                if (json?.channel) onChannelUpdated(json.channel);
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
                throw err;
            } finally {
                setSaving(false);
            }
        },
        [channelCode, vendor.id, onChannelUpdated, disabled],
    );

    const inputsDisabled = disabled || saving;

    const toggleEnabled = async () => {
        try { await patch({ enabled: !vendor.enabled }); } catch { /* surfaced */ }
    };

    const commitVendorId = async () => {
        const trimmed = vendorIdDraft.trim();
        if (!trimmed || trimmed === vendor.vendorId) {
            setEditingVendorId(false);
            setVendorIdDraft(vendor.vendorId);
            return;
        }
        if (!VENDOR_KEY_PATTERN.test(trimmed)) {
            setError(t("integrations_add_vendor_invalid"));
            return;
        }
        try {
            await patch({ vendorId: trimmed });
            setEditingVendorId(false);
        } catch { /* surfaced via `error` */ }
    };

    const handleWarehousesChange = async (next: string[]) => {
        const current = vendor.fulfilmentWarehouses ?? [];
        // No-op if unchanged (same members in the same priority order).
        if (next.length === current.length && next.every((w, i) => w === current[i])) return;
        try { await patch({ fulfilmentWarehouses: next }); } catch { /* surfaced */ }
    };

    return (
        <div className="admin-integrations-vendor-row" id={`vendor-${vendor.id}`}>
            <div className="admin-integrations-vendor-header">
                <label className="admin-settings-toggle" title={t("integrations_vendor_enabled_hint")}>
                    <input
                        type="checkbox"
                        checked={vendor.enabled}
                        onChange={toggleEnabled}
                        disabled={inputsDisabled}
                    />
                    <span className="admin-settings-slider" />
                </label>
                <span className="admin-integrations-vendor-name">{vendor.displayName}</span>
                {saving && <Loader2 size={14} className="admin-integrations-spinner" />}
            </div>

            <div className="admin-integrations-vendor-body">
                <span className="admin-integrations-vendor-label">
                    {t("integrations_field_vendor_id")}:
                </span>
                {editingVendorId ? (
                    <div className="admin-integrations-vendor-edit">
                        <input
                            type="text"
                            className="admin-integrations-input admin-integrations-input--mono"
                            value={vendorIdDraft}
                            onChange={e => setVendorIdDraft(e.target.value.toLowerCase())}
                            placeholder={t("integrations_field_vendor_id_placeholder")}
                            maxLength={128}
                            autoFocus
                            disabled={inputsDisabled}
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={commitVendorId}
                            disabled={
                                saving
                                || vendorIdDraft.trim().length === 0
                                || !VENDOR_KEY_PATTERN.test(vendorIdDraft.trim())
                            }
                            aria-label={t("save")}
                        >
                            <Check size={14} />
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => { setEditingVendorId(false); setVendorIdDraft(vendor.vendorId); }}
                            disabled={inputsDisabled}
                            aria-label={t("cancel")}
                        >
                            <X size={14} />
                        </button>
                    </div>
                ) : (
                    <div className="admin-integrations-vendor-display">
                        <code className="admin-integrations-vendor-id-code">
                            {vendor.vendorId || <em>{t("integrations_secret_unset")}</em>}
                        </code>
                        <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setEditingVendorId(true)}
                            disabled={inputsDisabled}
                            aria-label={t("integrations_secret_rotate")}
                        >
                            <Pencil size={12} />
                        </button>
                    </div>
                )}
            </div>

            <div className="admin-integrations-vendor-body">
                <span className="admin-integrations-vendor-label">
                    {t("integrations_vendor_warehouses")}:
                </span>
                <WarehouseMultiSelect
                    options={warehouses}
                    selected={vendor.fulfilmentWarehouses ?? []}
                    onChange={handleWarehousesChange}
                    disabled={inputsDisabled}
                />
            </div>

            {error && <p className="admin-integrations-field-error">{error}</p>}
        </div>
    );
};

export default VendorSubAccountRow;
